import { ChangeEventHandler } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { getFromDataPath } from "../../utility";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";

export interface ArsBooleanProps {
  /** RFC 6901 JSON Pointer path to the boolean value in ArsContext state */
  dataPath: string;
  /** Optional flag to disable the control */
  disabled?: boolean;
  /** When true, a false value is treated as undefined */
  falseIsUndefined?: boolean;
  /** Unique identifier for the control */
  id: string;
  /** Label for the control */
  label: string;
  /** Optional width of the label column */
  labelWidth?: number;
  /** Optional maximum width of the control column */
  maxWidthCols?: number;
}

/**
 * A boolean input control that reads from and writes to ArsContext state using a JSON Pointer path.
 * Uses PATCH_ARS_STATE action to update the state value at the specified dataPath.
 *
 * @param props - Component props extending Control213Props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the boolean value in ArsContext state.
 * @param props.disabled - Optional flag to disable the control.
 * @param props.falseIsUndefined - Optional flag to treat false as undefined when updating state.
 * @param props.id - Unique identifier for the control.
 * @param props.label - Label for the control.
 * @param props.labelWidth - Optional width of the label column.
 * @param props.maxWidthCols - Optional maximum width of the control column.
 * @returns A React functional component rendering a Control213 integrated with ArsContext.
 */
export const ArsBoolean = ({
  dataPath,
  disabled = false,
  falseIsUndefined = false,
  id,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
}: ArsBooleanProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const value = getFromDataPath(dataPath, state, (v) => typeof v === "boolean") ?? false;

  /**
   * Handles updating the boolean value in ArsContext state.
   *
   * @param e - The change event from the checkbox input.
   */
  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.checked;
    const updatedValue = falseIsUndefined && newValue === false ? undefined : newValue;
    dispatch({
      operation: PATCH_ARS_STATE,
      dataPath,
      value: updatedValue,
    });
  };

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="mb-1"
    >
      <Form.Label
        column
        md={labelWidth}
      >
        {label}
      </Form.Label>
      <Col
        md={{ span: maxWidthCols - labelWidth }}
        className="d-flex align-items-center"
      >
        <Form.Check
          type="switch"
          name={id}
          checked={value}
          onChange={handleChange}
          disabled={disabled}
        />
      </Col>
    </Form.Group>
  );
};

ArsBoolean.displayName = "ArsBoolean";
